import React, {
  useState,
  useEffect,
  useCallback
} from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import PropTypes from "prop-types";
import clsx from "clsx";
import * as Yup from "yup";
import { Field, FieldArray, Formik } from "formik";
import { useSnackbar } from "notistack";
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import axios from 'src/utils/axios';
import useIsMountedRef from 'src/hooks/useIsMountedRef';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  Popover,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";

import TreeView from '@mui/lab/TreeView';
import TreeItem from '@mui/lab/TreeItem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DataGrid } from '@mui/x-data-grid';

import { makeStyles } from "@mui/styles";
import { createProduct, updateProduct } from "src/actions/mgmtProductAction";
import "./helper.css";
import MperitoCard from 'src/components/MperitoCard';
import { formatDateToYYYYMMDD, getCurrentDate } from 'src/utils/dateUtils';
import ImageEditor from 'src/components/ImageEditor';
import { IMAGE_TYPE } from "src/config";
import ProductImages from './ProductImages';
import mgmtVariant from 'src/services/mgmtVariant';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";

const useStyles = makeStyles(() => ({
  root: {},
  editor: {
    "& .ql-editor": {
      height: 400,
    },
  },
}));


const IMAGE_PATH = process.env.REACT_APP_PATH;

function ProductCreateForm({ className, customerId, productInfo, isEngineeringType, categories, userType, ...rest }) {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [openVariantDialog, setOpenVariantDialog] = useState(false);

const [variantForm, setVariantForm] = useState({
  variantName: "",
  variantSKU: "",
  weight: "",
  sellPrice: "",
  availableStock: "",
  description: ""
});

  const account = useSelector((state) => state.account);
  const addedBy = account.user.email;
  const isMountedRef = useIsMountedRef();

  // Category Handling Code below - start
  const [anchorEl, setAnchorEl] = React.useState(null);

  const categoryList = categories;

  const [variantOptions, setVariantOptions] = useState([]);
  const [loadingVariants, setLoadingVariants] = useState(false);

  const [productTypes, setProductTypes] = useState([]);


  const handleSelectCategory = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id1 = open ? "simple-popover" : undefined;

  const renderTree = (nodes) => (
    <TreeItem key={nodes.categoryId} nodeId={nodes.categoryId} label={nodes.name}>
      {Array.isArray(nodes.children)
        ? nodes.children.map((node) => renderTree(node))
        : null}
    </TreeItem>
  );

  // Category Handling Code below - end

  const date = getCurrentDate();

  const [image, setImage] = useState('')

  function handleImage(e) {
    console.log(e.target.files)
    setImage(e.target.files[0])
  }

  const [files, setFiles] = useState([]);

  let additionalFeatures = [];
  if (productInfo) {
    let inAdditionalFeatures = productInfo?.additionalFeatures ?? [];
    additionalFeatures = inAdditionalFeatures.map((item) => {
      const newObj = {};
      for (const key in item) {
        newObj.Key = key;
        newObj.Value = Array.isArray(item[key]) ? item[key].join(',') : item[key].toString();
      }
      return newObj;
    });
  }

  // Fetch all variant options from backend API
  const getAllVariantOptions = useCallback(() => {
    setLoadingVariants(true);
    axios
      .get(`/api/admin/catalog/product/variant/options/all`)
      .then((response) => {
        console.log('✅ Full API Response:', response.data);

        if (isMountedRef.current) {
          if (response?.data?.payload?.variantOptions && Array.isArray(response.data.payload.variantOptions)) {
            const allOptions = response.data.payload.variantOptions;

            // Transform all variant options into a structured format
            const transformedOptions = allOptions.map(variant => ({
              name: variant.name,
              variationId: variant.variationId,
              customerId: variant.customerId,
              values: variant.values.map((value, index) => ({
                value: value,
                index: index
              }))
            }));

            setVariantOptions(transformedOptions);
            console.log('✅ All variant options loaded:', transformedOptions);
          } else {
            console.warn('⚠️ Invalid API response structure');
            setVariantOptions([]);
          }
        }
      })
      .catch((error) => {
        console.error('❌ Error fetching variant options:', error);
        enqueueSnackbar("Failed to load variant options", { variant: "error" });
        setVariantOptions([]);
      })
      .finally(() => setLoadingVariants(false));
  }, [isMountedRef, enqueueSnackbar]);

const getAllProductTypes = useCallback(() => {
  setProductTypes(["SIMPLE", "CONFIGURABLE"]);
}, []);



useEffect(() => {
  getAllVariantOptions();
  getAllProductTypes();
}, [getAllVariantOptions, getAllProductTypes]);


  function uploadFileHandler(event) {
    setFiles(event.target.files);
  };

  if (loadingVariants) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  let additionalFeaturesData = [];
  if (productInfo) {
    let inAdditionalFeatures = productInfo?.additionalFeatures ?? [];
    additionalFeaturesData = inAdditionalFeatures.map((item) => {
      const newObj = {};
      for (const key in item) {
        newObj.Key = key;
        newObj.Value = Array.isArray(item[key]) ? item[key].join(',') : item[key].toString();
      }
      return newObj;
    });
  }

  // Create initial values for variants dynamically
  const getInitialVariantValues = () => {
    const variantValues = {};
    variantOptions.forEach(variant => {
      const fieldName = variant.name.toLowerCase();
      variantValues[fieldName] = productInfo ? (productInfo[fieldName] || '') : '';
    });
    return variantValues;
  };

  const getInitialProductType = () => {
  return productInfo?.productType || '';
};


  return (

    <Formik
      enableReinitialize
      initialValues={{
        customerId: customerId,
        productId: productInfo ? productInfo.productId : '',
        categoryId: productInfo ? productInfo.categoryId : [],
        make: productInfo ? productInfo.make : "",
        productType: getInitialProductType(), // ✅ product type
        productName: productInfo ? productInfo.productName : "",
        availableStock: productInfo ? productInfo.availableStock : 0,
        brandName: productInfo ? productInfo.brandName : "",
        ...getInitialVariantValues(), // Add all variant fields dynamically
        productSKU: productInfo ? productInfo.productSKU : "",
        baseImageUrl: productInfo ? IMAGE_PATH + "/" + productInfo.baseImageUrl : "",
        description: productInfo ? productInfo.description : "",
        header: productInfo ? productInfo.header : "",
        metrics: productInfo ? productInfo.metrics : "",
        amPartNo: productInfo ? productInfo.amPartNo : "",
        imageURL: productInfo ? productInfo.imageURL : "",
        images: productInfo ? productInfo.images : "",
        sellPrice: productInfo ? productInfo.price?.sellPrice : 0,
        specialPrice: productInfo ? productInfo.price?.specialPrice?.price : 0,
        discount: productInfo ? productInfo.price?.specialPrice?.discount : 0,
        from: productInfo ? formatDateToYYYYMMDD(productInfo.price?.specialPrice?.from) : date || "",
        till: productInfo ? formatDateToYYYYMMDD(productInfo.price?.specialPrice?.till) : "",
        additionalFeatures: productInfo ? additionalFeaturesData : [
          {
            Key: "",
            Value: "",
          }]

      }}
      validationSchema={Yup.object().shape({
        productName: Yup.string().max(50).required("Product Name is required"),
        brandName: Yup.string().max(50).required("Brand Name is required"),
        specialPrice: Yup.string().max(10).required("Price is required"),
        description: Yup.string().max(500).required("description is required"),
        productSKU: Yup.string().max(50).required("productSKU is required"),
        make: Yup.string().max(50).required("Make is required"),
        categoryId: Yup.array().min(1, "Please select the category").required("Category is required")
      })}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        let newAdditionalFeatures = [];
        let newObj;
        values.additionalFeatures.map((val, ind, arr) => {
          if (val.Key && val.Value) {
            newObj = {}

            const myArray = val.Value.split(",");
            if (myArray.length > 1) {
              newObj[val.Key] = myArray;
            } else {
              newObj[val.Key] = myArray[0];
            }
            newAdditionalFeatures.push(newObj);
          }
        });

        values.additionalFeatures = newAdditionalFeatures;
        try {
          if (!productInfo) {
            await dispatch(createProduct(addedBy, values, image, files, customerId, userType));
            setStatus({ success: true });
            setSubmitting(false);
            enqueueSnackbar("Product Created", {
              variant: "success",
            });
          } else {
            values.baseImageFile = image;
            values.productImages = Array.from(files);
            await dispatch(updateProduct(values, customerId, userType));
            setStatus({ success: true });
            setSubmitting(false);
            enqueueSnackbar("Product Updated", {
              variant: "success",
            });
          }

          navigate("/app/management/product/view");
        } catch (err) {
          setErrors({ submit: err.responce?.data?.message || "An error occurred" });
          setStatus({ success: false });
          setSubmitting(false);
          enqueueSnackbar("Failed to Add the Product", {
            variant: "error",
          });
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        touched,
        values,
      }) => (
        <form
          onSubmit={handleSubmit}
          className={clsx(classes.root, className)}
          {...rest}
        >
          <Grid container spacing={2} >
            <Grid item md={8} >
              <MperitoCard title="Product Information " >
                <Grid item xs={6}>
                  <TextField
                    error={Boolean(
                      touched.productName && errors.productName
                    )}
                    fullWidth
                    helperText={
                      touched.productName && errors.productName
                    }
                    required
                    label="Product Name"
                    name="productName"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.productName}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    error={Boolean(
                      touched.brandName && errors.brandName
                    )}
                    fullWidth
                    helperText={
                      touched.brandName && errors.brandName
                    }
                    required
                    label="Brand Name"
                    name="brandName"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.brandName}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    error={Boolean(
                      touched.productSKU && errors.productSKU
                    )}
                    fullWidth
                    helperText={
                      touched.productSKU && errors.productSKU
                    }
                    required
                    label="Product SKU"
                    name="productSKU"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.productSKU}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    error={Boolean(
                      touched.header && errors.header
                    )}
                    fullWidth
                    helperText={
                      touched.header && errors.header
                    }
                    required
                    label="Header"
                    name="header"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.header}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    error={Boolean(
                      touched.make && errors.make
                    )}
                    fullWidth
                    helperText={
                      touched.make && errors.make
                    }
                    required
                    label="Make"
                    name="make"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.make}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    error={Boolean(
                      touched.metrics && errors.metrics
                    )}
                    fullWidth
                    helperText={
                      touched.metrics && errors.metrics
                    }
                    label="Metrics"
                    name="metrics"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.metrics}
                    variant="outlined"
                  />
                </Grid>
                {isEngineeringType &&
                  <Grid item xs={6}>
                    <TextField
                      error={Boolean(
                        touched.amPartNo && errors.amPartNo
                      )}
                      fullWidth
                      helperText={
                        touched.amPartNo && errors.amPartNo
                      }
                      label="AM Part No"
                      name="amPartNo"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.amPartNo}
                      variant="outlined"
                    />
                  </Grid>}
                <Grid item xs={12}>
                  <TextField
                    error={Boolean(
                      touched.description && errors.description
                    )}
                    fullWidth
                    helperText={
                      touched.description && errors.description
                    }
                    required
                    multiline
                    rows={4}
                    label="Description"
                    name="description"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.description}
                    variant="outlined"
                  />
                </Grid>

              </MperitoCard>

            </Grid>
            <Grid item md={4} >
              <MperitoCard title="Product Pricing/Quantity" >
                <Grid item md={12}>
                  <TextField
                    error={Boolean(
                      touched.sellPrice && errors.sellPrice
                    )}
                    fullWidth
                    helperText={
                      touched.sellPrice && errors.sellPrice
                    }
                    required
                    label="Price"
                    type="number"
                    name="sellPrice"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.sellPrice}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={12}>
                  <TextField
                    error={Boolean(
                      touched.availableStock && errors.availableStock
                    )}
                    fullWidth
                    helperText={
                      touched.availableStock && errors.availableStock
                    }
                    required
                    label="Available Stock"
                    name="availableStock"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.availableStock}
                    variant="outlined"
                  />
                </Grid>
              </MperitoCard>

{productTypes.length > 0 && (
  <MperitoCard title="Product Type" sx={{ mt: 2 }}>
    <Grid container spacing={2}>
      <Grid item xs={12} >
        <FormControl fullWidth>
          <InputLabel id="product-type-label">
            Select Product Type
          </InputLabel>
          <Select
            labelId="product-type-label"
            name="productType"
            value={values.productType}
            label="Select Product Type"
            onChange={handleChange}
            onBlur={handleBlur}
          >
            {productTypes.map((type) => (
  <MenuItem key={type} value={type}>
    {type}
  </MenuItem>
))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  </MperitoCard>
)}

   {/* Product Variants Card */}
              {!productInfo && values.productType === "CONFIGURABLE" && variantOptions && variantOptions.length > 0 && (
                <MperitoCard title="Product Variants" sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    {variantOptions.map((variant) => (
                      <Grid item xs={12} sm={6} md={3} key={variant.variationId}>
                        <FormControl fullWidth>
                          <InputLabel id={`${variant.name}-select-label`}>
                            Select {variant.name.charAt(0).toUpperCase() + variant.name.slice(1)}
                          </InputLabel>
                          <Select
                            labelId={`${variant.name}-select-label`}
                            id={`${variant.name}-select`}
                            label={`Select ${variant.name.charAt(0).toUpperCase() + variant.name.slice(1)}`}
                            name={variant.name.toLowerCase()}
                            value={values[variant.name.toLowerCase()]}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <MenuItem value="">
                              <em>Select {variant.name}</em>
                            </MenuItem>
                            {variant.values.map((option) => (
                              <MenuItem key={option.index} value={option.index}>
                                {option.value}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {values[variant.name.toLowerCase()] !== '' &&
                         values[variant.name.toLowerCase()] !== null &&
                         values[variant.name.toLowerCase()] !== undefined && (
                          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            Selected: <strong>
                              {variant.values.find(v => v.index === values[variant.name.toLowerCase()])?.value || 'N/A'}
                            </strong>
                          </Typography>
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </MperitoCard>
              )}
              <MperitoCard title="Special Pricing" sx={{ mt: 2 }}>
                <Grid item xs={6}>
                  <TextField
                    error={Boolean(
                      touched.specialPrice && errors.specialPrice
                    )}
                    fullWidth
                    helperText={
                      touched.specialPrice && errors.specialPrice
                    }
                    label="Price"
                    type="number"
                    name="specialPrice"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.specialPrice}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    error={Boolean(
                      touched.discount && errors.discount
                    )}
                    fullWidth
                    helperText={
                      touched.discount && errors.discount
                    }
                    label="Discount"
                    type="number"
                    name="discount"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.discount}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    error={Boolean(
                      touched.from && errors.from
                    )}
                    fullWidth
                    helperText={
                      touched.from && errors.from
                    }
                    type="date"
                    name="from"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.from}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    error={Boolean(
                      touched.till && errors.till
                    )}
                    fullWidth
                    helperText={
                      touched.till && errors.till}
                    type="date"
                    name="till"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.till}
                    variant="outlined"
                  />
                </Grid>
              </MperitoCard>
            </Grid>
            <Grid item md={8} >
              <MperitoCard title="Product Attributes" >
                <Grid container xs={12} spacing={2} margin={2}>
                  <FieldArray name="additionalFeatures">
                    {fieldArrayProps => (
                      <Grid container item spacing={2} >
                        <Grid item xs={12}>

                          <p>Click below button to add one more key and value </p>
                          <Button
                            color="secondary"
                            variant="contained"
                            type="button"
                            onClick={

                              () => {
                                fieldArrayProps.push({ Key: "", Value: "" });
                              }

                            }  >
                            +
                          </Button>
                        </Grid>

                        {values.additionalFeatures.map((option, index) => (
                          <Grid container spacing={2} margin={1} key={index}>
                            <Grid item xs={12} sm={5.5} >
                              <Field name={`additionalFeatures.${index}.Key`} minWidth>
                                {fieldProps => (
                                  <div>
                                    <input placeholder="Key" {...fieldProps.field} />
                                  </div>
                                )}
                              </Field>
                            </Grid>


                            <Grid item xs={12} sm={5.5} >
                              <Field name={`additionalFeatures.${index}.Value`} minWidth>
                                {fieldProps => (
                                  <div>
                                    <input placeholder="Value" {...fieldProps.field} />
                                  </div>
                                )}
                              </Field>
                            </Grid>
                            <Grid item xs={12} sm={1}>
                              <Button
                                color="secondary"
                                variant="contained"
                                type="button"
                                style={{ marginLeft: "0em" }}
                                onClick={() => fieldArrayProps.remove(index)}
                              >
                                -
                              </Button>
                            </Grid>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </FieldArray>
                </Grid>
              </MperitoCard>
            </Grid>
            <Grid item md={4} >

              <MperitoCard title="Catalog Mapping" >
                <Grid container xs={12} spacing={2} margin={2}>
                  <TextField
                    fullWidth
                    multiple
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    label="Select Category"
                    name="categoryId"
                    value={values.categoryId}
                    className="w-100"
                    onClick={handleSelectCategory}
                  />
                  {touched.categoryId && errors.categoryId && (
                  <FormHelperText error>
                   {errors.categoryId}
                   </FormHelperText>
                 )}
                  <Popover
                    id={id1}
                    fullWidth
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left"
                    }}
                  >
                    <TreeView
                      multiple
                      aria-label="rich object"
                      defaultCollapseIcon={<ExpandMoreIcon />}
                      defaultExpanded={['root']}
                      defaultExpandIcon={<ChevronRightIcon />}
                      sx={{ height: 200, flexGrow: 1, width: 300, overflowY: 'auto' }}
                      onNodeSelect={(e, categoryId) => {
                        setFieldValue("categoryId", [categoryId]);
                        }}

                    >
                      {categoryList.map((category) => (
                        renderTree(category)
                      ))}
                    </TreeView>
                  </Popover>
                  <Typography mt={1}>{productInfo?.categoryNames?.join(" -> ")}</Typography>

                </Grid>
              </MperitoCard>
            </Grid>

            <Grid item md={12} >
              <MperitoCard title="Product Images" >
                <Grid item xs={12}>
                  <Typography>Base Image</Typography>
                  <TextField
                    error={Boolean(
                      touched.baseImageUrl && errors.baseImageUrl
                    )}
                    fullWidth
                    helperText={
                      touched.baseImageUrl && errors.baseImageUrl
                    }
                    name="baseImageUrl"
                    type="file"
                    onBlur={handleBlur}
                    onChange={handleImage}
                    value={values.image}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography>Sub Image</Typography>
                  <input type="file" multiple onChange={uploadFileHandler} />
                </Grid>

                <Grid item xs={12}>
                  <ImageEditor type={IMAGE_TYPE.BASEIMAGE} inImage={values.baseImageUrl} customerId={customerId} id={productInfo?.productId} />
                </Grid>
                <Grid container xs={12} >
                  <ProductImages productInfo={productInfo} customerId={customerId} IMAGE_PATH={IMAGE_PATH} />
                </Grid>
              </MperitoCard>
            </Grid>
          </Grid>

           {errors.submit && (
            <Box mt={3}>
              <FormHelperText error>{errors.submit}</FormHelperText>
            </Box>
          )}
          <Box mt={2}>
            <Button
              color="secondary"
              variant="contained"
              type="submit"
              disabled={isSubmitting}

            >
              {!productInfo ? "Create Product" : "Update Product"}
            </Button>
          </Box>
{/* Product Variants List */}
{ productInfo?.productItems?.productVariants ?.length > 0 && (
    <MperitoCard title="Product Variants" sx={{ mt: 2 }} >
    <Box
  sx={{
    width: "100%",
    display: "flex",
    justifyContent: "flex-end",
    mb: 2
  }}
>
  <Button
    variant="contained"
    color="primary"
    onClick={() => setOpenVariantDialog(true)}
  >
    + Add Variant
  </Button>
</Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <DataGrid
            rows={
              productInfo?.productItems
                ?.productVariants || []
            }
            columns={[
              {
                field: 'productVariantId',
                headerName: 'Variant Id',
                width: 130
              },
              {
                field: 'variantName',
                headerName: 'Variant Name',
                width: 180
              },
              {
                field: 'variantSKU',
                headerName: 'Variant SKU',
                width: 180
              },
              {
                field: 'size',
                headerName: 'Size',
                width: 120,
                valueGetter: (params) => {
                  const sizeObj =
                    params.row.variations?.find(
                      (item) => item.size
                    );
                  return (
                    sizeObj?.size || '-'
                  );
                }
              },
              {
                field: 'weight',
                headerName: 'Weight',
                width: 130,
                valueGetter: (params) => {
                  const weightObj =
                    params.row.variations?.find(
                      (item) =>
                        item.weight ||
                        item.WEIGHT
                    );
                  return (
                    weightObj?.weight ||
                    weightObj?.WEIGHT ||
                    '-'
                  );
                }
              },
              {
                field: 'colour',
                headerName: 'Colour',
                width: 130,
                valueGetter: (params) => {
                  const colourObj =
                    params.row.variations?.find(
                      (item) => item.colour
                    );
                  return (
                    colourObj?.colour || '-'
                  );
                }
              },
              {
                field: 'type',
                headerName: 'Type',
                width: 180,
                valueGetter: (params) => {
                  const typeObj =
                    params.row.variations?.find(
                      (item) => item.type
                    );
                  return (
                    typeObj?.type || '-'
                  );
                }
              },
              {
                field: 'availableStock',
                headerName: 'Available Stock',
                width: 170
              },
              {
                field: 'minStock',
                headerName: 'Min Stock',
                width: 130
              },
              {
                field: 'baseProductId',
                headerName: 'Base Product Id',
                width: 170
              },
              {
                field: 'price',
                headerName: 'Price',
                width: 120,
                valueGetter: (params) => {
                  return (
                    params.row.price
                      ?.sellPrice || 0
                  );
                }
              }
            ]}
            getRowId={(row) =>
              row.productVariantId
            }
            autoHeight
            pageSize={5}
            rowsPerPageOptions={[5]}
          />
        </Grid>
      </Grid>
    </MperitoCard>
)}
<Dialog
  open={openVariantDialog}
  onClose={() => setOpenVariantDialog(false)}
  maxWidth="sm"
  fullWidth
>
  <DialogTitle>Add Variant</DialogTitle>

  <DialogContent>

    <TextField
  fullWidth
  margin="normal"
  label="Base Product Id"
  value={productInfo?.productId || ""}
  InputProps={{
    readOnly: true
  }}
/>

    <TextField
      fullWidth
      margin="normal"
      label="Variant Name"
      value={variantForm.variantName}
      onChange={(e) =>
        setVariantForm({
          ...variantForm,
          variantName: e.target.value
        })
      }
    />

    <TextField
      fullWidth
      margin="normal"
      label="Variant SKU"
      value={variantForm.variantSKU}
      onChange={(e) =>
        setVariantForm({
          ...variantForm,
          variantSKU: e.target.value
        })
      }
    />

    <TextField
      fullWidth
      margin="normal"
      label="Weight"
      value={variantForm.weight}
      onChange={(e) =>
        setVariantForm({
          ...variantForm,
          weight: e.target.value
        })
      }
    />

    <TextField
      fullWidth
      margin="normal"
      label="Price"
      type="number"
      value={variantForm.sellPrice}
      onChange={(e) =>
        setVariantForm({
          ...variantForm,
          sellPrice: e.target.value
        })
      }
    />

    <TextField
      fullWidth
      margin="normal"
      label="Available Stock"
      type="number"
      value={variantForm.availableStock}
      onChange={(e) =>
        setVariantForm({
          ...variantForm,
          availableStock: e.target.value
        })
      }
    />

    <TextField
      fullWidth
      margin="normal"
      label="Description"
      multiline
      rows={3}
      value={variantForm.description}
      onChange={(e) =>
        setVariantForm({
          ...variantForm,
          description: e.target.value
        })
      }
    />

  </DialogContent>

  <DialogActions>

    <Button
      onClick={() => setOpenVariantDialog(false)}
    >
      Cancel
    </Button>

    <Button
      variant="contained"
      onClick={async () => {

        const productData = {
          customerId: customerId,
          baseProductId: productInfo.productId,
          variantName: variantForm.variantName,
          variantSKU: variantForm.variantSKU,
          variations: [
            {
              weight: variantForm.weight
            }
          ],
          price: {
            sellPrice: Number(
              variantForm.sellPrice
            )
          },
          description:
            variantForm.description,
          availableStock: Number(
            variantForm.availableStock
          )
        };

        const formData = new FormData();

        formData.append(
          "productData",
          JSON.stringify(productData)
        );

        try {

          await mgmtVariant.addVariant(
            formData
          );

          enqueueSnackbar(
            "Variant Added Successfully",
            { variant: "success" }
          );

          setOpenVariantDialog(false);

        } catch (error) {

          enqueueSnackbar(
            "Failed to add variant",
            { variant: "error" }
          );

        }
      }}
    >
      Save
    </Button>

  </DialogActions>

</Dialog>

        </form>
      )}
    </Formik>
  );
}

ProductCreateForm.propTypes = {
  className: PropTypes.string,
};

export default ProductCreateForm;

